import { createSlice } from '@reduxjs/toolkit';
import type { RootState } from 'redux/store';
import { createAsyncThunkWrapper } from 'redux/thunk/createAsyncThunkWrapper';
import { parishSelectionMoved } from 'redux/selectors/parishContext';
import { refreshMeStaffRoles } from 'redux/slices/user/userSlice';
import { updateTableState } from 'redux/slices/common/appSlice';
import { notify } from 'components/Common/Toast';
import { getProblemDetail, type StaffRoleType } from 'api/v1Helpers';
import {
    fetchStaff,
    addStaff,
    removeStaffMember,
    createInvitation,
    revokeInvitation,
    fetchInvitations,
    provisionStaffAccount,
    type StaffMember,
    type Invitation,
    type ProvisionedStaffAccount,
} from 'services/staffApi';

interface StaffState {
    items: StaffMember[];
    itemsTotal: number;
    loading: boolean;
    error: string | null;
    invitations: Invitation[];
    invitationsTotal: number;
    invitationsLoading: boolean;
    loadedParishId: string | null;
    /** Parish id of the latest in-flight list request (race guard). */
    inflightParishId: string | null;
    invitationsInflightParishId: string | null;
    /** requestId of the latest dispatched invitations fetch — drops an older request's response
     *  that resolves after a newer one (e.g. rapid Next-Page clicks), matching the guard
     *  pattern used by parishOrganizationSlice / parishMemberSlice / announcementGroupSlice. */
    invitationsRequestId: string | null;
}

const initialState: StaffState = {
    items: [],
    itemsTotal: 0,
    loading: false,
    error: null,
    invitations: [],
    invitationsTotal: 0,
    invitationsLoading: false,
    loadedParishId: null,
    inflightParishId: null,
    invitationsInflightParishId: null,
    invitationsRequestId: null,
};

export const getStaffList = createAsyncThunkWrapper<{ items: StaffMember[]; total: number; parishId: string } | null, { parishId: string }>(
    'staff/getStaffList',
    async ({ parishId }, { getState }) => {
        if (!parishId) return null;
        try {
            // Deliberately not overriding pageSize (unlike getInvitations) — fetchStaff's own
            // default of 100 is left as-is. The Staff tab stays client-paginated (Next/Prev
            // never re-fetches), so unlike Invitations there's no path that could recover
            // records beyond whatever's requested here if fetchStaff's "backend ignores paging,
            // returns everything" comment ever turns out to be stale — shrinking this to match
            // a small "rows per page" UI value would risk silently truncating the staff list.
            const res = await fetchStaff(parishId);
            if (parishSelectionMoved(getState, parishId)) return null;
            return { items: res.items, total: res.total, parishId };
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to load staff' }
);

export const assignStaff = createAsyncThunkWrapper<StaffMember | null, { parishId: string; userId: string; role: StaffRoleType }>(
    'staff/assignStaff',
    async ({ parishId, userId, role }, { getState, dispatch }) => {
        if (!parishId) return null;
        try {
            const result = await addStaff(parishId, userId, role);
            if (parishSelectionMoved(getState, parishId)) return null;
            notify('Staff member assigned successfully', 'success');
            dispatch(refreshMeStaffRoles());
            return result;
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to assign staff role' }
);

export const removeStaff = createAsyncThunkWrapper<{ parishId: string } | null, { parishId: string; userId: string }>(
    'staff/removeStaff',
    async ({ parishId, userId }, { getState, dispatch }) => {
        if (!parishId || !userId) return null;
        try {
            await removeStaffMember(parishId, userId);
            if (parishSelectionMoved(getState, parishId)) return null;
            notify('Staff member removed successfully', 'success');
            dispatch(refreshMeStaffRoles());
            return { parishId };
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to remove staff member' }
);

export const sendStaffInvitation = createAsyncThunkWrapper<Invitation | null, { parishId: string; email: string; role: StaffRoleType }>(
    'staff/sendInvitation',
    async ({ parishId, email, role }, { getState, dispatch }) => {
        if (!parishId) return null;
        try {
            const result = await createInvitation(parishId, email, role);
            if (parishSelectionMoved(getState, parishId)) return null;
            notify('Invitation created successfully. The invitation email is being sent.', 'success');
            dispatch(getInvitations({ parishId, status: 'PENDING' }));
            return result;
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to send invitation' }
);

/** Primary Create Staff — provisions User + StaffRole + credentials/access email. */
export const provisionStaff = createAsyncThunkWrapper<
    ProvisionedStaffAccount | null,
    { parishId: string; firstName: string; lastName: string; email: string; role: StaffRoleType }
>(
    'staff/provisionStaff',
    async ({ parishId, firstName, lastName, email, role }, { getState, dispatch }) => {
        if (!parishId) return null;
        try {
            const result = await provisionStaffAccount(parishId, { firstName, lastName, email, role });
            if (parishSelectionMoved(getState, parishId)) return null;
            notify(
                result.kind === 'granted'
                    ? 'Staff role granted. An access notification has been queued for email.'
                    : 'Staff account created. Login instructions have been queued for email.',
                'success'
            );
            dispatch(getStaffList({ parishId }));
            dispatch(refreshMeStaffRoles());
            return result;
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to create staff account' }
);

export const getInvitations = createAsyncThunkWrapper<
    { items: Invitation[]; total: number; parishId: string } | null,
    { parishId: string; status?: string }
>(
    'staff/getInvitations',
    async ({ parishId, status }, { getState }) => {
        if (!parishId) return null;
        try {
            // Server-paginated: read page/pageSize fresh from the shared table slice so the
            // request matches what's actually selected in "Rows per page", and Staff.tsx's fetch
            // effect re-fires on pageNumber changes too — so unlike the old "fetch once, slice
            // client-side forever" version, requesting a small pageSize no longer risks stranding
            // records beyond it: Next/Prev now fetches the next page for real.
            const { pageNumber = 0, pageSize = 20 } = (getState() as RootState).app.table;
            const res = await fetchInvitations(parishId, { page: pageNumber + 1, pageSize, status });
            if (parishSelectionMoved(getState, parishId)) return null;
            return { items: res.items, total: res.total, parishId };
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to load invitations' }
);

export const revokeStaffInvitation = createAsyncThunkWrapper<{ parishId: string } | null, { parishId: string; invitationId: string }>(
    'staff/revokeInvitation',
    async ({ parishId, invitationId }, { getState, dispatch }) => {
        if (!parishId || !invitationId) return null;
        try {
            await revokeInvitation(parishId, invitationId);
            if (parishSelectionMoved(getState, parishId)) return null;
            notify('Invitation revoked', 'success');
            // Revoking the only/last row on the current page would otherwise leave the shared
            // table-pagination slice pointing past the end of the now-shorter client-sliced
            // list, rendering the empty-state branch even though earlier pages still have rows.
            // Dispatched before the refetch below (synchronous store update lands before that
            // thunk's own logic runs) so there's no extra request involved.
            const { pageNumber = 0, pageSize = 20 } = (getState() as RootState).app.table;
            if (pageNumber !== 0) {
                dispatch(updateTableState({ pageNumber: 0, pageSize, offset: 0 }));
            }
            dispatch(getInvitations({ parishId }));
            return { parishId };
        } catch (err) {
            if (parishSelectionMoved(getState, parishId)) return null;
            throw err;
        }
    },
    { parseErrorMessage: (err) => getProblemDetail(err) || 'Failed to revoke invitation' }
);

const staffSlice = createSlice({
    name: 'staff',
    initialState,
    reducers: {
        clearStaff(state) {
            state.items = [];
            state.itemsTotal = 0;
            state.invitations = [];
            state.invitationsTotal = 0;
            state.loadedParishId = null;
            state.inflightParishId = null;
            state.invitationsInflightParishId = null;
            state.invitationsRequestId = null;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getStaffList.pending, (state, action) => {
            state.loading = true;
            state.error = null;
            state.items = [];
            state.inflightParishId = action.meta.arg.parishId;
        });
        builder.addCase(getStaffList.fulfilled, (state, action) => {
            if (!action.payload) {
                if (state.inflightParishId === action.meta.arg.parishId) {
                    state.loading = false;
                }
                return;
            }
            const { items, total, parishId } = action.payload;
            if (parishId !== state.inflightParishId) return;
            state.loading = false;
            state.items = items;
            state.itemsTotal = total;
            state.loadedParishId = parishId;
        });
        builder.addCase(getStaffList.rejected, (state, action) => {
            if (state.inflightParishId === action.meta.arg.parishId) {
                state.loading = false;
                state.error = (action.payload as string) || 'Failed to load staff';
            }
        });

        builder.addCase(getInvitations.pending, (state, action) => {
            state.invitationsLoading = true;
            // Only blank the list on a parish switch — a same-parish refetch (e.g. Next/Prev,
            // or after a revoke) keeps showing the current page's rows under the loading
            // overlay instead of flashing empty then repainting.
            if (state.invitationsInflightParishId !== action.meta.arg.parishId) {
                state.invitations = [];
            }
            state.invitationsRequestId = action.meta.requestId;
            state.invitationsInflightParishId = action.meta.arg.parishId;
        });
        builder.addCase(getInvitations.fulfilled, (state, action) => {
            // Drop a response for a request that's since been superseded (e.g. a second
            // Next-Page click fired before the first request resolved) so a slower, older
            // response can't overwrite newer data.
            if (action.meta.requestId !== state.invitationsRequestId) return;
            state.invitationsLoading = false;
            if (!action.payload) return;
            state.invitations = action.payload.items;
            state.invitationsTotal = action.payload.total;
        });
        builder.addCase(getInvitations.rejected, (state, action) => {
            if (action.meta.requestId !== state.invitationsRequestId) return;
            state.invitationsLoading = false;
        });
    },
});

export const { clearStaff } = staffSlice.actions;
export default staffSlice.reducer;
